// Ambient fauna — fish jumps (+ splash rings) and paddling ducks, slices
// W1+W2 of docs/02-systems/ambient-fauna.md. Sites/assignments come from the
// pure layout module; this component only animates: fish arcs are parabolic
// hops timed by a per-site cycle, ducks orbit their site with a gentle bob.
// All instanced, hard-capped, and gated behind Settings → "Ambient life".

import type { SceneNode } from '@aod/protocol';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { useSettings } from '../../settings/useSettings.js';
import { hexNoise } from '../elevation.js';
import {
  type Duck,
  FISH_CAP,
  type FishJump,
  type Grazer,
  ducks,
  fishJumps,
  foxRoutes,
  pigSties,
  rabbitWarrens,
  sheepHerds,
  waterSites,
} from './layout.js';

const JUMP_SECONDS = 0.6; // airtime of one fish arc
const JUMP_HEIGHT = 0.22;
const FISH_COLOR = '#3d4f58';
const DUCK_COLOR = '#4a3b2a';
const SPLASH_COLOR = '#dfeef0';

const fishGeo = new THREE.BoxGeometry(0.085, 0.018, 0.03);
const duckGeo = mergeDuck();
const ringGeo = new THREE.RingGeometry(0.03, 0.045, 20).rotateX(-Math.PI / 2);

function mergeDuck(): THREE.BufferGeometry {
  // Body + head + bill in one geometry (single color; silhouette is what
  // reads — a wake behind it sells "duck" more than plumage).
  const body = new THREE.SphereGeometry(0.045, 6, 5).scale(1.5, 0.85, 1).translate(0, 0.02, 0);
  const head = new THREE.SphereGeometry(0.026, 6, 5).translate(0.05, 0.055, 0);
  const bill = new THREE.BoxGeometry(0.024, 0.008, 0.016).translate(0.075, 0.05, 0);
  return mergeGeometries([body, head, bill]) ?? body;
}

const fishMat = new THREE.MeshBasicMaterial({ color: FISH_COLOR });
const duckMat = new THREE.MeshBasicMaterial({ color: DUCK_COLOR });
const ringMat = new THREE.MeshBasicMaterial({
  color: SPLASH_COLOR,
  transparent: true,
  opacity: 0.8,
  depthWrite: false,
});

function FaunaBatch({
  jumps,
  duckAssignments,
}: {
  readonly jumps: readonly FishJump[];
  readonly duckAssignments: readonly Duck[];
}) {
  const fishRef = useRef<THREE.InstancedMesh>(null);
  const duckRef = useRef<THREE.InstancedMesh>(null);
  const ringRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    // Hide unused slots once on mount (scale 0) — useFrame keeps them hidden.
    for (const mesh of [fishRef.current, duckRef.current, ringRef.current]) {
      if (!mesh) continue;
      const zero = new THREE.Matrix4().makeScale(0, 0, 0);
      for (let i = 0; i < mesh.count; i++) mesh.setMatrixAt(i, zero);
      mesh.instanceMatrix.needsUpdate = true;
    }
  }, [jumps.length, duckAssignments.length]);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const obj = new THREE.Object3D();

    // Fish arcs: slot i is in the air for JUMP_SECONDS of every `period`.
    const fish = fishRef.current;
    const ring = ringRef.current;
    if (fish && ring) {
      jumps.forEach((jump, i) => {
        const cycle = (t / jump.period + jump.phase) % 1;
        const u = (cycle * jump.period) / JUMP_SECONDS;
        if (u >= 1) {
          obj.scale.setScalar(0);
          obj.updateMatrix();
          fish.setMatrixAt(i, obj.matrix);
          ring.setMatrixAt(i, obj.matrix);
          return;
        }
        // Arc: parabola over the jump, heading from the site's noise.
        const heading = hexNoise(jump.site.x, jump.site.z) * Math.PI * 2;
        const travel = 0.12;
        obj.position.set(
          jump.site.x + Math.cos(heading) * travel * (u - 0.5),
          jump.site.y + 0.02 + JUMP_HEIGHT * 4 * u * (1 - u),
          jump.site.z + Math.sin(heading) * travel * (u - 0.5),
        );
        obj.rotation.set(0, -heading, Math.sin(u * Math.PI) * 0.9);
        obj.scale.setScalar(1);
        obj.updateMatrix();
        fish.setMatrixAt(i, obj.matrix);
        // Splash ring blooms where the fish lands, over the last 20%.
        const splashU = u >= 0.8 ? (u - 0.8) / 0.2 : 0;
        if (splashU > 0) {
          obj.position.set(
            jump.site.x + Math.cos(heading) * travel * 0.5,
            jump.site.y + 0.015,
            jump.site.z + Math.sin(heading) * travel * 0.5,
          );
          obj.rotation.set(0, 0, 0);
          obj.scale.setScalar(1 + splashU * 4);
          obj.updateMatrix();
          ring.setMatrixAt(i, obj.matrix);
          ringMat.opacity = 0.8 * (1 - splashU);
        } else {
          obj.scale.setScalar(0);
          obj.updateMatrix();
          ring.setMatrixAt(i, obj.matrix);
        }
      });
      fish.instanceMatrix.needsUpdate = true;
      ring.instanceMatrix.needsUpdate = true;
    }

    // Ducks: orbit the site, bob gently, face the direction of travel.
    const duckMesh = duckRef.current;
    if (duckMesh) {
      duckAssignments.forEach((duck, i) => {
        const angle = duck.startAngle + t * duck.speed;
        const x = duck.site.x + Math.cos(angle) * duck.radius;
        const z = duck.site.z + Math.sin(angle) * duck.radius;
        const bob = Math.sin(t * 2.2 + duck.startAngle * 3) * 0.008;
        obj.position.set(x, duck.site.y + 0.01 + bob, z);
        // Tangent heading (direction of travel around the orbit).
        obj.rotation.set(0, -angle - (Math.sign(duck.speed) * Math.PI) / 2, 0);
        obj.scale.setScalar(1);
        obj.updateMatrix();
        duckMesh.setMatrixAt(i, obj.matrix);
      });
      duckMesh.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <group>
      <instancedMesh
        args={[fishGeo, fishMat, Math.max(jumps.length, FISH_CAP)]}
        ref={fishRef}
        frustumCulled={false}
      />
      <instancedMesh
        args={[ringGeo, ringMat, Math.max(jumps.length, FISH_CAP)]}
        ref={ringRef}
        frustumCulled={false}
      />
      <instancedMesh
        args={[duckGeo, duckMat, Math.max(duckAssignments.length, 1)]}
        ref={duckRef}
        frustumCulled={false}
      />
    </group>
  );
}

export function FaunaLayer({
  tiles,
  biomeMap,
  visibleCoords,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly biomeMap: Map<string, string>;
  readonly visibleCoords?: ReadonlySet<string> | null;
}) {
  const showFauna = useSettings((s) => s.showFauna);

  const visibleTiles = useMemo(() => {
    if (!visibleCoords) return tiles;
    return tiles.filter((t) => {
      const p = (t.props ?? {}) as Record<string, unknown>;
      const q = p.q as number | undefined;
      const r = p.r as number | undefined;
      return q === undefined || r === undefined || visibleCoords.has(`${q},${r}`);
    });
  }, [tiles, visibleCoords]);

  const { jumps, duckAssignments } = useMemo(() => {
    if (!showFauna) return { jumps: [], duckAssignments: [] };
    const sites = waterSites(visibleTiles, biomeMap);
    return { jumps: fishJumps(sites), duckAssignments: ducks(sites) };
  }, [visibleTiles, biomeMap, showFauna]);

  if (!showFauna) return null;
  return (
    <>
      <FaunaBatch jumps={jumps} duckAssignments={duckAssignments} />
      <LandFauna tiles={visibleTiles} biomeMap={biomeMap} />
    </>
  );
}

// ── Land cast (W3+W4): rabbits, the fox, sheep, pigs ─────────────────────

const RABBIT_COLOR = '#a08a68';
const FOX_COLOR = '#b8652f';
const SHEEP_COLOR = '#e8e4da';
const PIG_COLOR = '#c99a8e';

function animalGeo(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  return mergeGeometries(parts) ?? parts[0]!;
}

const rabbitGeo = animalGeo([
  new THREE.SphereGeometry(0.03, 6, 5).scale(1, 0.9, 1.3).translate(0, 0.028, 0),
  new THREE.BoxGeometry(0.008, 0.035, 0.004).translate(-0.011, 0.07, -0.012),
  new THREE.BoxGeometry(0.008, 0.035, 0.004).translate(0.011, 0.07, -0.012),
]);
const foxGeo = animalGeo([
  new THREE.SphereGeometry(0.045, 6, 5).scale(1, 0.85, 1.8).translate(0, 0.04, 0),
  new THREE.BoxGeometry(0.022, 0.022, 0.07).translate(0, 0.055, -0.11).rotateX(0.5),
  new THREE.BoxGeometry(0.014, 0.03, 0.006).translate(-0.018, 0.085, 0.05),
  new THREE.BoxGeometry(0.014, 0.03, 0.006).translate(0.018, 0.085, 0.05),
]);
const sheepGeo = animalGeo([
  new THREE.SphereGeometry(0.05, 6, 5).scale(1.25, 1, 1).translate(0, 0.055, 0),
  new THREE.BoxGeometry(0.026, 0.026, 0.03).translate(0.065, 0.055, 0),
]);
const pigGeo = animalGeo([
  new THREE.SphereGeometry(0.048, 6, 5).scale(1.4, 0.9, 1).translate(0, 0.045, 0),
  new THREE.BoxGeometry(0.02, 0.016, 0.02).translate(0.08, 0.035, 0),
]);

const rabbitMat = new THREE.MeshBasicMaterial({ color: RABBIT_COLOR });
const foxMat = new THREE.MeshBasicMaterial({ color: FOX_COLOR });
const sheepMat = new THREE.MeshBasicMaterial({ color: SHEEP_COLOR });
const pigMat = new THREE.MeshBasicMaterial({ color: PIG_COLOR });

const HOP_SECONDS = 0.35;
const HOP_HEIGHT = 0.06;
const HOP_DIST = 0.16;
const RABBIT_CYCLE = 3.5;
const GRAZE_CYCLE = 4.5;
const FOX_SPEED = 0.3; // tiles/s along the prowl loop

function LandFauna({
  tiles,
  biomeMap,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly biomeMap: Map<string, string>;
}) {
  const rabbits = useMemo(() => rabbitWarrens(tiles, biomeMap), [tiles, biomeMap]);
  const routes = useMemo(() => foxRoutes(tiles, biomeMap), [tiles, biomeMap]);
  const sheep = useMemo(() => sheepHerds(tiles, biomeMap), [tiles, biomeMap]);
  const pigs = useMemo(() => pigSties(tiles, biomeMap), [tiles, biomeMap]);

  const rabbitRef = useRef<THREE.InstancedMesh>(null);
  const foxRef = useRef<THREE.InstancedMesh>(null);
  const sheepRef = useRef<THREE.InstancedMesh>(null);
  const pigRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const zero = new THREE.Matrix4().makeScale(0, 0, 0);
    for (const mesh of [rabbitRef.current, foxRef.current, sheepRef.current, pigRef.current]) {
      if (!mesh) continue;
      for (let i = 0; i < mesh.count; i++) mesh.setMatrixAt(i, zero);
      mesh.instanceMatrix.needsUpdate = true;
    }
  }, [rabbits.length, routes.length, sheep.length, pigs.length]);

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const obj = new THREE.Object3D();

    // Rabbits: nibble (mostly still, tiny wobble) with a short hop to a new
    // spot each cycle, heading from the rabbit's seed.
    const rabbitMesh = rabbitRef.current;
    if (rabbitMesh) {
      rabbits.forEach((rabbit, i) => {
        const heading = hexNoise(rabbit.anchor.x + i * 7, rabbit.anchor.z - i * 5) * Math.PI * 2;
        const cycle = (t / RABBIT_CYCLE + rabbit.phase) % 1;
        const hopT = cycle * RABBIT_CYCLE;
        const x = rabbit.anchor.x + rabbit.ox;
        const z = rabbit.anchor.z + rabbit.oz;
        if (hopT < HOP_SECONDS) {
          const u = hopT / HOP_SECONDS;
          obj.position.set(
            x + Math.cos(heading) * HOP_DIST * (u - 0.5),
            rabbit.anchor.y + HOP_HEIGHT * 4 * u * (1 - u),
            z + Math.sin(heading) * HOP_DIST * (u - 0.5),
          );
          obj.rotation.set(0, -heading, 0);
        } else {
          // Nibble wobble.
          obj.position.set(x, rabbit.anchor.y, z);
          obj.rotation.set(Math.sin(t * 9 + i * 2.4) * 0.12, -heading, 0);
        }
        obj.scale.setScalar(1);
        obj.updateMatrix();
        rabbitMesh.setMatrixAt(i, obj.matrix);
      });
      rabbitMesh.instanceMatrix.needsUpdate = true;
    }

    // Fox: trot the closed loop with a little pounce arc over each node.
    const foxMesh = foxRef.current;
    if (foxMesh) {
      routes.forEach((route, i) => {
        const pts = route.points;
        const segLens: number[] = [];
        let loop = 0;
        for (let s = 0; s < pts.length; s++) {
          const a = pts[s]!;
          const b = pts[(s + 1) % pts.length]!;
          const len = Math.hypot(b.x - a.x, b.z - a.z) || 0.001;
          segLens.push(len);
          loop += len;
        }
        let walked = (((t * FOX_SPEED + route.phase * loop) % loop) + loop) % loop;
        let s = 0;
        while (walked > segLens[s]!) {
          walked -= segLens[s]!;
          s = (s + 1) % pts.length;
        }
        const a = pts[s]!;
        const b = pts[(s + 1) % pts.length]!;
        const u = walked / segLens[s]!;
        const pounce = Math.sin(u * Math.PI) * 0.05;
        obj.position.set(a.x + (b.x - a.x) * u, a.y + 0.02 + pounce, a.z + (b.z - a.z) * u);
        obj.rotation.set(0, -Math.atan2(b.z - a.z, b.x - a.x), 0);
        obj.scale.setScalar(1);
        obj.updateMatrix();
        foxMesh.setMatrixAt(i, obj.matrix);
      });
      foxMesh.instanceMatrix.needsUpdate = true;
    }

    // Sheep and pigs: small wander around the herd anchor + periodic graze
    // dip (sheep) / shuffle step (pigs).
    const herds: readonly [THREE.InstancedMesh | null, readonly Grazer[], boolean][] = [
      [sheepRef.current, sheep, true],
      [pigRef.current, pigs, false],
    ];
    for (const [mesh, grazers, graze] of herds) {
      if (!mesh) continue;
      grazers.forEach((g, i) => {
        const wander = Math.sin(t * (graze ? 0.35 : 0.6) + g.phase * 9);
        const wanderZ = Math.cos(t * (graze ? 0.29 : 0.53) + g.phase * 7);
        const x = g.anchor.x + g.ox + wander * 0.06;
        const z = g.anchor.z + g.oz + wanderZ * 0.06;
        // Graze: dip forward ~1 s of every cycle; pigs shuffle-step instead.
        const c = (t / GRAZE_CYCLE + g.phase) % 1;
        const dip = graze ? (c > 0.72 ? Math.sin(((c - 0.72) / 0.28) * Math.PI) * 0.45 : 0) : 0;
        const step = graze ? 0 : Math.max(0, Math.sin(c * Math.PI * 2 - 1.2)) * 0.03;
        obj.position.set(x + Math.cos(g.phase * 6.3) * step, g.anchor.y, z);
        obj.rotation.set(dip, g.phase * Math.PI * 2, 0);
        obj.scale.setScalar(1);
        obj.updateMatrix();
        mesh.setMatrixAt(i, obj.matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <group>
      <instancedMesh
        args={[rabbitGeo, rabbitMat, Math.max(rabbits.length, 1)]}
        ref={rabbitRef}
        frustumCulled={false}
      />
      <instancedMesh
        args={[foxGeo, foxMat, Math.max(routes.length, 1)]}
        ref={foxRef}
        frustumCulled={false}
      />
      <instancedMesh
        args={[sheepGeo, sheepMat, Math.max(sheep.length, 1)]}
        ref={sheepRef}
        frustumCulled={false}
      />
      <instancedMesh
        args={[pigGeo, pigMat, Math.max(pigs.length, 1)]}
        ref={pigRef}
        frustumCulled={false}
      />
    </group>
  );
}
